// The template editor's field groups, one card each. Purely presentational:
// every section binds to the editor's single form through `control`, so the
// live preview sees each change as it happens.
//
// Grouping rule: a card holds one decision and everything that shapes it —
// the logo image sits with its position, the background image with its
// colour. Splitting those across cards (or, as before, across two components
// with different save models) is what made the page hard to read.

import { Card, Input } from "@pej/kore/components";
import type { Control, FieldPath } from "react-hook-form";
import { BrandColorField } from "@/components/brand-color-field";
import { CssEditor } from "@/components/css-editor";
import { Field } from "@/lib/forms";
import { ArtworkSlot } from "./artwork-field";
import { FontPicker } from "./font-picker";
import { OWN_FONT, type TemplateValues } from "./template-schema";

interface SectionProps {
    control: Control<TemplateValues>;
}

interface ColorFieldProps {
    name: FieldPath<TemplateValues>;
    /** Role caption rendered inside the control (vouchers-style). */
    caption: string;
    control: Control<TemplateValues>;
    description?: string;
}

/** One brand-color box: swatch, role, and editable hex in a single control. */
function ColorField({ name, caption, control, description }: ColorFieldProps) {
    return (
        <Field
            name={name}
            control={control}
            description={description}
            render={({ field: { value, onChange, onBlur } }) => (
                <BrandColorField caption={caption} value={String(value)} onChange={onChange} onBlur={onBlur} />
            )}
        />
    );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
    return (
        <Card className="p-6">
            <h2 className="text-base font-semibold">{title}</h2>
            {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
            <div className="mt-4 flex flex-col gap-4">{children}</div>
        </Card>
    );
}

export function NameSection({ control }: SectionProps) {
    return (
        <Section title="Template" description="The name you'll recognise this template by.">
            <Field name="name" control={control} label="Name" required render={({ field }) => <Input {...field} />} />
        </Section>
    );
}

export function BackgroundSection({ control }: SectionProps) {
    return (
        <Section title="Background" description="The surface behind everything. An image covers the colour.">
            <ColorField name="backgroundColor" caption="Background" control={control} />
            <Field
                name="backgroundImageAssetId"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <ArtworkSlot
                        assetType="IMAGE"
                        value={String(value)}
                        onChange={onChange}
                        fieldLabel="Background image"
                        pickerTitle="Choose a background"
                        unavailableLabel="Background image"
                    />
                )}
            />
        </Section>
    );
}

export function TypeSection({
    control,
    fontChoice,
    ownFontUrl,
}: SectionProps & { fontChoice: string; ownFontUrl: string | null }) {
    return (
        <Section title="Type" description="The typeface and colour every line of text inherits.">
            <Field
                name="fontChoice"
                control={control}
                label="Font"
                render={({ field: { value, onChange } }) => (
                    <FontPicker value={String(value)} onChange={onChange} ownFontUrl={ownFontUrl} />
                )}
            />
            {fontChoice === OWN_FONT && (
                <Field
                    name="fontAssetId"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <ArtworkSlot
                            assetType="FONT"
                            value={String(value)}
                            onChange={onChange}
                            pickerTitle="Choose a font"
                            unavailableLabel="Your font"
                        />
                    )}
                />
            )}
            <ColorField
                name="slotText"
                caption="Text"
                control={control}
                description="Used by every line of text, including the big number. To colour the number on its own, use Template CSS below."
            />
        </Section>
    );
}

export function CssSection({ control }: SectionProps) {
    return (
        <Section
            title="Template CSS"
            description="Optional styling for anything the fields above don't cover, shared by every screen using this template. The examples are commented out — uncomment one to start."
        >
            <Field
                name="customCss"
                control={control}
                label="Template CSS"
                optional
                render={({ field: { value, onChange } }) => (
                    <CssEditor value={String(value)} onChange={onChange} ariaLabel="Template CSS" />
                )}
            />
        </Section>
    );
}
